import React from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';

// Tooltip com portal e posicionamento inteligente para evitar cortes pelo card
const InfoTooltip = ({ content, className = '', placement = 'right' }) => {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef(null);
  const tooltipRef = React.useRef(null);

  const updatePosition = React.useCallback(() => {
    const icon = triggerRef.current;
    const tip = tooltipRef.current;
    if (!icon) return;
    const rect = icon.getBoundingClientRect();
    const gap = 8;
    let top = rect.top + window.scrollY - 8;
    let left = placement === 'right' ? rect.right + window.scrollX + gap : rect.left + window.scrollX - gap;
    // Se já renderizou, ajustar para não estourar viewport
    if (tip) {
      const tRect = tip.getBoundingClientRect();
      const vw = window.innerWidth + window.scrollX;
      const vh = window.innerHeight + window.scrollY;
      // Rebater para a esquerda se sair da direita
      if (left + tRect.width > vw - 8) {
        left = rect.left + window.scrollX - tRect.width - gap;
      }
      // Rebater para baixo se sair por cima
      if (top + tRect.height > vh - 8) {
        top = rect.bottom + window.scrollY - tRect.height;
      }
      if (top < window.scrollY + 4) {
        top = rect.top + window.scrollY;
      }
    }
    setPos({ top, left });
  }, [placement]);

  React.useEffect(() => {
    if (!open) return;
    updatePosition();
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize, true);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  const handleMouseEnter = () => setOpen(true);
  const handleMouseLeave = () => setOpen(false);
  const handleClick = () => setOpen((v) => !v);

  return (
    <span className={`relative inline-flex items-center ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Informações"
        className="ml-1 inline-flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <Icon name="Info" size={14} />
      </button>
      {open && createPortal(
        <span
          ref={tooltipRef}
          role="tooltip"
          style={{ position: 'absolute', top: `${pos.top}px`, left: `${pos.left}px` }}
          className={
            'fixed z-[100] max-w-[280px] whitespace-normal break-words rounded-md bg-popover text-popover-foreground px-3 py-2 text-sm shadow-elevation-2 border border-border pointer-events-auto'
          }
        >
          {content}
        </span>,
        document.body
      )}
    </span>
  );
};

export default InfoTooltip;